import { supabase } from '../lib/supabase';
import errorTrackingService from './errorTrackingService';

export const offerService = {
  /**
   * Upload contract PDF to storage
   */
  async uploadContractPdf(propertyId, offerId, pdfFile) {
    try {
      // Create file path: propertyId/offerId-timestamp.pdf
      const timestamp = Date.now();
      const fileName = `${offerId}-${timestamp}.pdf`;
      const filePath = `${propertyId}/${fileName}`;

      // Upload to storage
      const { data, error } = await supabase?.storage
        ?.from('contract-pdfs')
        ?.upload(filePath, pdfFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      return { data: { path: filePath }, error: null };
    } catch (error) {
      console.error('Upload contract PDF error:', error);
      errorTrackingService?.captureApiError(
        '/storage/contract-pdfs',
        'POST',
        error,
        { propertyId, offerId, fileName: pdfFile?.name },
        null
      );
      return { data: null, error: error?.message || 'Failed to upload contract PDF' };
    }
  },

  /**
   * Get signed URL for contract PDF (private bucket)
   */
  async getContractPdfUrl(filePath) {
    try {
      if (!filePath) {
        return { data: null, error: 'No file path provided' };
      }

      const { data, error } = await supabase?.storage
        ?.from('contract-pdfs')
        ?.createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) throw error;

      return { data: data?.signedUrl, error: null };
    } catch (error) {
      console.error('Get contract PDF URL error:', error);
      errorTrackingService?.captureApiError(
        '/storage/contract-pdfs/signed-url',
        'GET',
        error,
        { filePath },
        null
      );
      return { data: null, error: error?.message || 'Failed to get PDF URL' };
    }
  },

  /**
   * Get signed URL for contract PDF with 1-week expiration (for report generation)
   */
  async getContractPdfUrlForReport(filePath) {
    try {
      if (!filePath) {
        return { data: null, error: 'No file path provided' };
      }

      // 1 week = 7 days * 24 hours * 60 minutes * 60 seconds = 604800 seconds
      const ONE_WEEK_IN_SECONDS = 604800;

      const { data, error } = await supabase?.storage
        ?.from('contract-pdfs')
        ?.createSignedUrl(filePath, ONE_WEEK_IN_SECONDS);

      if (error) throw error;

      return { data: data?.signedUrl, error: null };
    } catch (error) {
      console.error('Get contract PDF URL for report error:', error);
      errorTrackingService?.captureApiError(
        '/storage/contract-pdfs/signed-url-report',
        'GET',
        error,
        { filePath },
        null
      );
      return { data: null, error: error?.message || 'Failed to get PDF URL for report' };
    }
  },

  /**
   * Delete contract PDF from storage
   */
  async deleteContractPdf(filePath) {
    try {
      if (!filePath) {
        return { error: null }; // Nothing to delete
      }

      const { error } = await supabase?.storage
        ?.from('contract-pdfs')
        ?.remove([filePath]);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error('Delete contract PDF error:', error);
      errorTrackingService?.captureApiError(
        '/storage/contract-pdfs',
        'DELETE',
        error,
        { filePath },
        null
      );
      return { error: error?.message || 'Failed to delete contract PDF' };
    }
  },

  /**
   * Get all offers for a property
   */
  async getOffers(propertyId) {
    try {
      const { data, error } = await supabase?.from('offers')?.select('*')?.eq('property_id', propertyId)?.order('display_order', { ascending: true });

      if (error) {
        console.error('[Offer Service] Error fetching offers:', error);
        
        // Provide user-friendly error messages for common issues
        if (error?.message?.includes('fetch') || error?.message?.includes('Failed to fetch')) {
          throw new Error('Network error: Unable to fetch offers. Please check your connection and try again.');
        } else if (error?.message?.includes('CORS')) {
          throw new Error('Connection error: Unable to reach the server. Please try again.');
        }
        
        throw error;
      }

      // Transform data to match component expectations
      const offers = data?.map(offer => ({
        id: offer?.id,
        buyerName: offer?.buyer_name,
        offerPrice: offer?.offer_price,
        downPayment: offer?.down_payment,
        financingType: offer?.financing_type,
        earnestMoney: offer?.earnest_money,
        closingDate: offer?.closing_date,
        contingencies: offer?.contingencies || [],
        appraisalContingency: offer?.appraisal_contingency,
        inspectionPeriod: offer?.inspection_period,
        sellerConcessions: offer?.seller_concessions,
        buyerAgent: offer?.buyer_agent,
        preApprovalAmount: offer?.pre_approval_amount,
        escalationClause: offer?.escalation_clause,
        submittedDate: offer?.submitted_date,
        expirationDate: offer?.expiration_date,
        internalNotes: offer?.internal_notes,
        contractPdfUrl: offer?.contract_pdf_url,
        balanceOfPurchase: offer?.balance_of_purchase,
        buyersAgentCommission: offer?.buyers_agent_commission
      }));

      return { data: offers, error: null };
    } catch (error) {
      console.error('Get offers error:', error);
      return { data: null, error: error?.message || 'Failed to fetch offers' };
    }
  },

  /**
   * Get a single offer by ID
   */
  async getOffer(offerId) {
    try {
      const { data, error } = await supabase?.from('offers')?.select('*')?.eq('id', offerId)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Get offer error:', error);
      return { data: null, error: error?.message || 'Failed to fetch offer' };
    }
  },

  /**
   * Create a new offer
   */
  async createOffer(propertyId, offerData) {
    console.log('[offerService] ========== CREATE OFFER ==========');
    console.log('[offerService] Timestamp:', new Date()?.toISOString());
    console.log('[offerService] Property ID:', propertyId);
    console.log('[offerService] Offer data:', {
      buyerName: offerData?.buyerName,
      offerPrice: offerData?.offerPrice,
      downPayment: offerData?.downPayment,
      financingType: offerData?.financingType,
      earnestMoney: offerData?.earnestMoney,
      closingDate: offerData?.closingDate,
      contingenciesCount: offerData?.contingencies?.length,
      sellerConcessions: offerData?.sellerConcessions,
      buyerAgent: offerData?.buyerAgent,
      preApprovalAmount: offerData?.preApprovalAmount,
      balanceOfPurchase: offerData?.balanceOfPurchase,
      buyersAgentCommission: offerData?.buyersAgentCommission
    });
    
    try {
      console.log('[offerService] Fetching existing offers to determine display order...');
      // Get the highest display_order for this property
      const { data: existingOffers } = await supabase?.from('offers')?.select('display_order')?.eq('property_id', propertyId)?.order('display_order', { ascending: false })?.limit(1);

      const nextOrder = existingOffers && existingOffers?.length > 0 
        ? existingOffers?.[0]?.display_order + 1 
        : 0;
      
      console.log('[offerService] Next display order:', nextOrder);
      console.log('[offerService] Inserting offer into database...');

      const { data, error } = await supabase?.from('offers')?.insert({
          property_id: propertyId,
          buyer_name: offerData?.buyerName,
          offer_price: offerData?.offerPrice,
          down_payment: offerData?.downPayment,
          financing_type: offerData?.financingType,
          earnest_money: offerData?.earnestMoney,
          closing_date: offerData?.closingDate,
          contingencies: offerData?.contingencies || [],
          appraisal_contingency: offerData?.appraisalContingency,
          inspection_period: offerData?.inspectionPeriod,
          seller_concessions: offerData?.sellerConcessions || 0,
          buyer_agent: offerData?.buyerAgent,
          pre_approval_amount: offerData?.preApprovalAmount,
          escalation_clause: offerData?.escalationClause || '',
          submitted_date: offerData?.submittedDate || new Date()?.toISOString()?.split('T')?.[0],
          expiration_date: offerData?.expirationDate,
          internal_notes: offerData?.internalNotes || '',
          ai_analysis: offerData?.analysis || null,
          contract_pdf_url: offerData?.contractPdfUrl || null,
          balance_of_purchase: offerData?.balanceOfPurchase || 0,
          buyers_agent_commission: offerData?.buyersAgentCommission || null,
          display_order: nextOrder
        })?.select()?.single();

      if (error) {
        console.error('[offerService] ❌ Database insert error:', error);
        console.error('[offerService] Error details:', {
          message: error?.message,
          code: error?.code,
          details: error?.details,
          hint: error?.hint
        });
        throw error;
      }

      console.log('[offerService] ✅ Offer created successfully');
      console.log('[offerService] Created offer ID:', data?.id);
      console.log('[offerService] Created offer data:', data);
      return { data, error: null };
    } catch (error) {
      console.error('[offerService] ❌ Create offer error:', error);
      console.error('[offerService] Error name:', error?.name);
      console.error('[offerService] Error message:', error?.message);
      console.error('[offerService] Error stack:', error?.stack);
      return { data: null, error: error?.message || 'Failed to create offer' };
    }
  },

  /**
   * Update an existing offer
   */
  async updateOffer(offerId, updates) {
    console.log('[offerService] ========== UPDATE OFFER ==========');
    console.log('[offerService] Timestamp:', new Date()?.toISOString());
    console.log('[offerService] Offer ID:', offerId);
    console.log('[offerService] Updates:', updates);
    
    try {
      const updateData = {
        buyer_name: updates?.buyerName,
        offer_price: updates?.offerPrice,
        down_payment: updates?.downPayment,
        financing_type: updates?.financingType,
        earnest_money: updates?.earnestMoney,
        closing_date: updates?.closingDate,
        contingencies: updates?.contingencies,
        appraisal_contingency: updates?.appraisalContingency,
        inspection_period: updates?.inspectionPeriod,
        seller_concessions: updates?.sellerConcessions,
        buyer_agent: updates?.buyerAgent,
        pre_approval_amount: updates?.preApprovalAmount,
        escalation_clause: updates?.escalationClause,
        expiration_date: updates?.expirationDate,
        internal_notes: updates?.internalNotes,
        balance_of_purchase: updates?.balanceOfPurchase,
        buyers_agent_commission: updates?.buyersAgentCommission
      };

      // Only include contract_pdf_url if it's explicitly provided
      if (updates?.contractPdfUrl !== undefined) {
        updateData.contract_pdf_url = updates?.contractPdfUrl;
      }

      const { data, error } = await supabase?.from('offers')?.update(updateData)?.eq('id', offerId)?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Update offer error:', error);
      return { data: null, error: error?.message || 'Failed to update offer' };
    }
  },

  /**
   * Update offer notes
   */
  async updateOfferNotes(offerId, notes) {
    try {
      const { data, error } = await supabase?.from('offers')?.update({ internal_notes: notes })?.eq('id', offerId)?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Update offer notes error:', error);
      return { data: null, error: error?.message || 'Failed to update notes' };
    }
  },

  /**
   * Delete an offer
   */
  async deleteOffer(offerId) {
    try {
      // Get the offer to find the PDF path
      const { data: offer } = await supabase?.from('offers')?.select('contract_pdf_url')?.eq('id', offerId)?.single();

      // Delete the PDF from storage if it exists
      if (offer?.contract_pdf_url) {
        await this.deleteContractPdf(offer?.contract_pdf_url);
      }

      // Delete the offer record
      const { error } = await supabase?.from('offers')?.delete()?.eq('id', offerId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error('Delete offer error:', error);
      return { error: error?.message || 'Failed to delete offer' };
    }
  },

  /**
   * Reorder offers (for drag and drop)
   */
  async reorderOffers(propertyId, offerIds) {
    try {
      // Update display_order for each offer
      const updates = offerIds?.map((offerId, index) => 
        supabase?.from('offers')?.update({ display_order: index })?.eq('id', offerId)?.eq('property_id', propertyId)
      );

      await Promise.all(updates);

      return { error: null };
    } catch (error) {
      console.error('Reorder offers error:', error);
      return { error: error?.message || 'Failed to reorder offers' };
    }
  },

  /**
   * Get offer count for a property
   */
  async getOfferCount(propertyId) {
    try {
      const { count, error } = await supabase?.from('offers')?.select('*', { count: 'exact', head: true })?.eq('property_id', propertyId);

      if (error) throw error;

      return { data: count, error: null };
    } catch (error) {
      console.error('Get offer count error:', error);
      return { data: 0, error: error?.message };
    }
  },

  // Check subscription limits for offer creation
  async checkOfferLimit(propertyId, userId) {
    try {
      // Get user profile with subscription info
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('subscription_tier, subscription_status')
        ?.eq('id', userId)
        ?.single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        return { allowed: false, error: profileError?.message };
      }

      const subscriptionTier = profile?.subscription_tier || 'free';
      const subscriptionStatus = profile?.subscription_status || 'inactive';

      // Pro users (active or trialing) have unlimited offers
      if (subscriptionTier === 'pro' && ['active', 'trialing']?.includes(subscriptionStatus)) {
        return { allowed: true, limit: null, current: null, tier: 'pro' };
      }

      // Free users: check if they have reached the 2 offers limit for this property
      const { count, error: countError } = await supabase
        ?.from('offers')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('property_id', propertyId);

      if (countError) {
        console.error('Error counting offers:', countError);
        return { allowed: false, error: countError?.message };
      }

      const currentCount = count || 0;
      const limit = 2; // Free tier limit: 2 offers per property

      return {
        allowed: currentCount < limit,
        limit: limit,
        current: currentCount,
        tier: 'free',
        needsUpgrade: currentCount >= limit
      };
    } catch (error) {
      console.error('Check offer limit error:', error);
      return { allowed: false, error: error?.message || 'Failed to check offer limit' };
    }
  },

  // Check if user can access offer comparison features (AI analysis)
  async checkComparisonAccess(userId) {
    try {
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('subscription_tier, subscription_status')
        ?.eq('id', userId)
        ?.single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        return { allowed: false, error: profileError?.message };
      }

      const subscriptionTier = profile?.subscription_tier || 'free';
      const subscriptionStatus = profile?.subscription_status || 'inactive';

      // Only Pro users with active or trialing status can access comparison features
      const hasAccess = subscriptionTier === 'pro' && ['active', 'trialing']?.includes(subscriptionStatus);

      return {
        allowed: hasAccess,
        tier: subscriptionTier,
        status: subscriptionStatus,
        needsUpgrade: !hasAccess
      };
    } catch (error) {
      console.error('Check comparison access error:', error);
      return { allowed: false, error: error?.message || 'Failed to check comparison access' };
    }
  }
};